import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronRight, MapPin, Briefcase, User } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { useConnectionStore } from '@/store/connectionStore';
import ConnectionButton from '../members/ConnectionButton';

interface RecommendedUser {
  id: string;
  auth_id: string;
  name: string;
  image: string;
  city: string;
  job_title: string;
  company: string;
  mutual_connections: number;
}

const RecommendedConnections: React.FC = () => {
  const [users, setUsers] = useState<RecommendedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { connections } = useConnectionStore();
  const [userCity, setUserCity] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchRecommendedUsers = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Get user's database ID
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, city')
          .eq('auth_id', user.id)
          .single();
          
        if (userError) throw userError;
        
        setUserCity(userData.city);
        
        // Fetch users from the same city who are not already connected
        const { data, error } = await supabase
          .from('users')
          .select(`
            id,
            auth_id,
            full_name,
            avatar_url,
            city,
            job_type,
            company
          `)
          .eq('city', userData.city)
          .neq('auth_id', user.id)
          .limit(5);
          
        if (error) throw error;
        
        // Filter out users who are already connected
        const connectedUserIds = connections
          .filter(conn => conn.status === 'accepted' || conn.status === 'pending')
          .map(conn => conn.other_user?.auth_id);
          
        const filteredUsers = data
          .filter(u => !connectedUserIds.includes(u.auth_id))
          .map(u => ({
            id: u.id,
            auth_id: u.auth_id,
            name: u.full_name,
            image: u.avatar_url || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png',
            city: u.city,
            job_title: u.job_type || 'Member',
            company: u.company || '',
            mutual_connections: Math.floor(Math.random() * 5) // Placeholder for mutual connections
          }));
          
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching recommended users:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRecommendedUsers();
  }, [connections]);
  
  // Listen for city-changed event
  useEffect(() => {
    const handleCityChanged = (e: CustomEvent) => {
      const { city } = e.detail;
      setUserCity(city);
      
      // Re-fetch recommended users when city changes
      fetchRecommendedUsers();
    };
    
    const fetchRecommendedUsers = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Get user's database ID
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, city')
          .eq('auth_id', user.id)
          .single();
          
        if (userError) throw userError;
        
        // Fetch users from the same city who are not already connected
        const { data, error } = await supabase
          .from('users')
          .select(`
            id,
            auth_id,
            full_name,
            avatar_url,
            city,
            job_type,
            company
          `)
          .eq('city', userData.city)
          .neq('auth_id', user.id)
          .limit(5);
          
        if (error) throw error;
        
        // Filter out users who are already connected
        const connectedUserIds = connections
          .filter(conn => conn.status === 'accepted' || conn.status === 'pending')
          .map(conn => conn.other_user?.auth_id);
          
        const filteredUsers = data
          .filter(u => !connectedUserIds.includes(u.auth_id))
          .map(u => ({
            id: u.id,
            auth_id: u.auth_id,
            name: u.full_name,
            image: u.avatar_url || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png',
            city: u.city,
            job_title: u.job_type || 'Member',
            company: u.company || '',
            mutual_connections: Math.floor(Math.random() * 5) // Placeholder for mutual connections
          }));
          
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching recommended users:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    window.addEventListener('city-changed', handleCityChanged as EventListener);
    
    return () => {
      window.removeEventListener('city-changed', handleCityChanged as EventListener);
    };
  }, [connections]);
  
  const handleUserClick = (userId: string) => {
    navigate(`/dashboard/members/${userId}`);
  };
  
  const handleViewAllClick = () => {
    navigate('/dashboard/match');
  };
  
  if (isLoading) {
    return (
      <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-40 bg-dark-300 rounded"></div>
          <div className="h-6 w-24 bg-dark-300 rounded"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-dark-300 rounded-lg h-24"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (users.length === 0) return null;
  
  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Users className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">
            People You May Know{userCity ? ` in ${userCity}` : ''}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      <div className="flex overflow-x-auto pb-2 space-x-4 no-scrollbar">
        {users.map((user) => (
          <div
            key={user.auth_id}
            className="bg-dark-300 rounded-lg p-4 hover:bg-dark-400 transition-colors border border-dark-400 hover:border-gold-400 flex-shrink-0 w-[280px]"
          >
            <div className="flex flex-col items-center">
              <div 
                className="mb-3 cursor-pointer flex-shrink-0"
                onClick={() => handleUserClick(user.auth_id)}
              >
                <img
                  src={user.image}
                  alt={user.name}
                  className={cn(
                    "w-24 h-24 rounded-full object-cover border-2 border-dark-400 hover:border-gold-400 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  )}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';
                  }}
                />
              </div>
              <div className="w-full text-center">
                <button
                  className="font-medium text-white hover:text-gold-400 transition-colors cursor-pointer text-center w-full"
                  onClick={() => handleUserClick(user.auth_id)}
                >
                  <span className="text-lg font-semibold">{user.name}</span>
                </button>
                <div className="flex items-center justify-center text-dark-800 text-xs mt-1">
                  <MapPin className="h-3 w-3 mr-1" />
                  <span className="truncate">{user.city}</span>
                </div>
                {(user.job_title || user.company) && (
                  <div className="flex items-center justify-center text-dark-800 text-xs mt-0.5">
                    <Briefcase className="h-3 w-3 mr-1" />
                    <span className="truncate">
                      {user.job_title}
                      {user.job_title && user.company && ' at '}
                      {user.company}
                    </span>
                  </div>
                )}
                {user.mutual_connections > 0 && (
                  <div className="flex items-center justify-center text-xs text-dark-800 mt-1">
                    <Users className="h-3 w-3 mr-1" />
                    <span>{user.mutual_connections} mutual connection{user.mutual_connections !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 pt-2 border-t border-dark-400">
              <ConnectionButton
                isConnected={false}
                memberId={user.auth_id}
                memberName={user.name}
                memberImage={user.image}
                className="w-full text-xs py-1.5"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecommendedConnections;